<template>
  <div class="d-inline-block print-receipt-button">
    <v-badge
      :color="'green'"
      :offset-x="badgeOffsetX"
      :offset-y="badgeOffsetY"
      :value="!canIssueDocument"
      overlap
      right
    >
      <template #badge>
        <v-icon
          dark
          v-text="'check'"
        />
      </template>

      <v-menu
        v-model="menuActive"
        :bottom="menuPosition === 'bottom'"
        :left="menuPosition === 'left'"
        :min-width="300"
        :nudge-width="300"
        :offset-x="menuOffsetX"
        :offset-y="menuOffsetY"
        :open-on-hover="openOnHover"
        :right="menuPosition === 'right'"
        :top="menuPosition === 'top'"
        auto
        class="fiscal-doc-menu"
        max-height="300"
      >
        <template #activator="{on: onMenu}">
          <v-tooltip
            :bottom="tooltipPosition === 'bottom'"
            :close-delay="tooltipCloseDelay"
            :color="color"
            :disabled="disabled || disableTooltip"
            :left="tooltipPosition === 'left'"
            :open-delay="tooltipOpenDelay"
            :right="tooltipPosition === 'right'"
            :top="tooltipPosition === 'top'"
          >
            <template #activator="{on: onTooltip}">
              <v-btn
                :absolute="absolute"
                :block="block"
                :class="[btnClass, icon ? color : '', {'grey lighten-2': disabled && icon}]"
                :color="icon ? '' : color"
                :dark="dark"
                :depressed="depressed"
                :disabled="disabled"
                :fab="fab"
                :fixed="fixed"
                :icon="icon"
                :large="large"
                :outlined="outline"
                :small="small"
                :style="btnStyle ? btnStyle : small ? icon ? `width: ${smallSize}px; height: ${smallSize}px;` : `height: ${smallSize}px;` : ''"
                :text="flat"
                :x-small="xSmall"
                v-on="{...onMenu, ...onTooltip}"
              >
                <v-icon
                  v-if="!hideIcon"
                  :left="!!labelText && !hideLabel && !icon"
                  :small="small && smallIcon"
                >
                  {{ iconName }}
                </v-icon>
                <span v-if="labelText && !hideLabel && !icon">{{ labelText }}</span>
              </v-btn>
            </template>

            <span>{{ tooltipText }}</span>
          </v-tooltip>
        </template>

        <!-- ISSUE DOCUMENT -->
        <v-list
          v-if="canIssueDocument"
          class="py-0"
        >
          <v-list-item
            v-if="fnb"
            :disabled="!fnb"
            @click="onIssueDocumentClick (FiscalDocTypeEnum.FNB)"
          >
            <v-list-item-action>
              <v-icon :color="fnb ? 'teal' : 'grey'">
                mdi-receipt-text-plus-outline
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ $t('Common.Button.IssueFnb') }}</v-list-item-title>
          </v-list-item>

          <template v-if="canIssueDocument">
            <v-list-item
              v-if="appHasReceiptSaleDocEnabled"
              :disabled="!receipt"
              @click="onIssueDocumentClick (FiscalDocTypeEnum.RECEIPT)"
            >
              <v-list-item-action>
                <v-icon :color="receipt ? 'green' : 'grey'">
                  mdi-receipt-text-plus-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.IssueReceiptSale') }}</v-list-item-title>
            </v-list-item>

            <template v-if="userIsSuperAdmin">
              <v-list-item
                v-if="appHasReceiptSaleDocEnabled"
                :disabled="!receipt"
                @click="onIssueDocumentClick (FiscalDocTypeEnum.RECEIPT, false)"
              >
                <v-list-item-action>
                  <v-icon :color="receipt ? 'green' : 'grey'">
                    mdi-receipt-text-plus-outline
                  </v-icon>
                </v-list-item-action>
                <v-list-item-title>{{ $t('Common.Button.IssueReceiptSale') }} (NO PRINT)</v-list-item-title>
              </v-list-item>
            </template>

            <v-list-item
              v-if="appHasReceiptServiceDocEnabled"
              :disabled="!receipt"
              @click="onIssueDocumentClick (FiscalDocTypeEnum.SERVICE_RECEIPT)"
            >
              <v-list-item-action>
                <v-icon :color="receipt ? 'green' : 'grey'">
                  mdi-receipt-text-plus-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.IssueReceiptService') }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="appHasReceiptCollectionDocEnabled"
              :disabled="!receipt"
              @click="onIssueDocumentClick (FiscalDocTypeEnum.COLLECTION_RECEIPT)"
            >
              <v-list-item-action>
                <v-icon :color="receipt ? 'green' : 'grey'">
                  mdi-receipt-text-plus-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.IssueReceiptCollection') }}</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="canIssueDocument">
            <v-list-item
              v-if="appHasReceiptSelfSupplyDocEnabled"
              :disabled="!receipt"
              @click="onIssueDocumentClick (FiscalDocTypeEnum.RECEIPT_SELF_SUPPLY)"
            >
              <v-list-item-action>
                <v-icon :color="receipt ? 'cyan' : 'grey'">
                  mdi-receipt-text-plus-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.IssueReceiptSelfSupply') }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="appHasReceiptSelfUseDocEnabled"
              :disabled="!receipt"
              @click="onIssueDocumentClick (FiscalDocTypeEnum.RECEIPT_SELF_USE)"
            >
              <v-list-item-action>
                <v-icon :color="receipt ? 'cyan' : 'grey'">
                  mdi-receipt-text-plus-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.IssueReceiptSelfUse') }}</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="canIssueDocument">
            <v-list-item
              v-if="appHasInvoiceSaleDocEnabled"
              :disabled="!invoice"
              @click="onIssueDocumentClick (FiscalDocTypeEnum.INVOICE)"
            >
              <v-list-item-action>
                <v-icon :color="invoice ? 'blue' : 'grey'">
                  mdi-invoice-text-plus-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.IssueInvoiceSale') }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="appHasInvoiceServiceDocEnabled"
              :disabled="!invoice"
              @click="onIssueDocumentClick (FiscalDocTypeEnum.SERVICE_INVOICE)"
            >
              <v-list-item-action>
                <v-icon :color="invoice ? 'blue' : 'grey'">
                  mdi-invoice-text-plus-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.IssueInvoiceService') }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>

        <!-- MANAGE ISSUED DOCUMENT -->
        <v-list
          v-else
          class="py-0"
        >
          <!-- PRINT DOCUMENT -->
          <v-list-item
            v-if="FiscalDocTypeEnum.isReceipt(documentType) && posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderFiscal"
            :disabled="!userCanPrintFiscalDoc"
            @click="onPrintDocumentClick(documentType)"
          >
            <v-list-item-action>
              <v-icon color="blue-grey">
                mdi-printer
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ FiscalDocTypeEnum.isSelfDoc(documentType) ? $t('Common.Button.PrintFiscalDoc') : $t('Common.Button.PrintReceipt') }}</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="FiscalDocTypeEnum.isInvoice(documentType) && posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderFiscal"
            :disabled="!userCanPrintFiscalDoc"
            @click="onPrintDocumentClick(documentType)"
          >
            <v-list-item-action>
              <v-icon color="blue-grey">
                mdi-printer
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ $t('Common.Button.PrintInvoice') }}</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="documentType === FiscalDocTypeEnum.FNB && posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderFiscal"
            :disabled="!userCanPrintFiscalDoc"
            @click="onPrintDocumentClick(documentType)"
          >
            <v-list-item-action>
              <v-icon color="blue-grey">
                mdi-printer
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ $t('Common.Button.PrintFnb') }}</v-list-item-title>
          </v-list-item>

          <!-- EMAIL DOCUMENT -->
          <template v-if="appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)">
            <v-list-item
              v-if="FiscalDocTypeEnum.isReceipt(documentType)"
              :disabled="!appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)"
              @click="onEmailDocumentClick(documentType)"
            >
              <v-list-item-action>
                <v-icon :color="appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL) ? 'blue' : 'grey'">
                  mdi-email-arrow-right
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ FiscalDocTypeEnum.isSelfDoc(documentType) ? $t('Common.Button.EmailDocument') : $t('Common.Button.EmailReceipt') }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="FiscalDocTypeEnum.isInvoice(documentType)"
              :disabled="!appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)"
              @click="onEmailDocumentClick(documentType)"
            >
              <v-list-item-action>
                <v-icon :color="appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL) ? 'blue' : 'grey'">
                  mdi-email-arrow-right
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.EmailInvoice') }}</v-list-item-title>
            </v-list-item>
          </template>

          <!-- VIEW DOCUMENT -->
          <template v-if="documentUrl">
            <v-list-item
              v-if="FiscalDocTypeEnum.isReceipt(documentType)"
              @click="onViewDocumentClick(documentUrl)"
            >
              <v-list-item-action>
                <v-icon color="light-green">
                  mdi-open-in-new
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ FiscalDocTypeEnum.isSelfDoc(documentType) ? $t('Common.Button.ViewFiscalDoc') : $t('Common.Button.ViewReceipt') }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="FiscalDocTypeEnum.isInvoice(documentType)"
              @click="onViewDocumentClick(documentUrl)"
            >
              <v-list-item-action>
                <v-icon color="light-green">
                  mdi-open-in-new
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.ViewInvoice') }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="documentType === FiscalDocTypeEnum.FNB"
              @click="onViewDocumentClick(documentUrl)"
            >
              <v-list-item-action>
                <v-icon color="light-green">
                  mdi-open-in-new
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.ViewFnb') }}</v-list-item-title>
            </v-list-item>
          </template>

          <v-divider />

          <!-- CONVERT RECEIPT TO INVOICE -->
          <v-list-item
            v-if="FiscalDocTypeEnum.isReceipt(documentType) && canConvertReceipt && ((documentType === FiscalDocTypeEnum.RECEIPT && appHasReceiptSaleDocEnabled && appHasInvoiceSaleDocEnabled) || (documentType === FiscalDocTypeEnum.SERVICE_RECEIPT && appHasReceiptServiceDocEnabled && appHasInvoiceServiceDocEnabled))"
            @click="onConvertReceiptToInvoiceClick(documentId)"
          >
            <v-list-item-action>
              <v-icon color="orange">
                mdi-file-document-refresh-outline
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ $t('Common.Button.ConvertReceipt') }}</v-list-item-title>
          </v-list-item>

          <!-- CANCEL DOCUMENT -->
          <v-list-item
            v-if="FiscalDocTypeEnum.isReceipt(documentType) && canCancelDocument"
            @click="onCancelDocumentClick(documentId)"
          >
            <v-list-item-action>
              <v-icon color="red">
                mdi-close-circle
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ FiscalDocTypeEnum.isSelfDoc(documentType) ? $t('Common.Button.CancelFiscalDoc') : $t('Common.Button.CancelReceipt') }}</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="FiscalDocTypeEnum.isInvoice(documentType) && canCancelDocument"
            @click="onCancelDocumentClick(documentId)"
          >
            <v-list-item-action>
              <v-icon color="red">
                mdi-close-circle
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ $t('Common.Button.CancelInvoice') }}</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="documentType === FiscalDocTypeEnum.FNB && canCancelFnb"
            @click="onCancelDocumentClick(documentId)"
          >
            <v-list-item-action>
              <v-icon color="red">
                mdi-close-circle
              </v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ $t('Common.Button.CancelFnb') }}</v-list-item-title>
          </v-list-item>

          <template v-if="userIsSuperAdmin && hasValidDeliveryCardTransaction()">
            <v-divider />

            <v-list-item
              @click="onUpdateDocumentPaymentMethodsClick(orderId)"
            >
              <v-list-item-action>
                <v-icon color="purple">
                  mdi-receipt-text-clock-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.UpdatePaymentMethods') }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>
    </v-badge>
  </div>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import NotifyTypeEnum from '@/api/Enums/NotifyTypeEnum'
import PinManagerEnum from '@/api/Enums/PinManagerEnum.js'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum.js'

export default {
  name      : 'IssueReceiptDocumentButton',
  components: {},
  directives: {},
  mixins    : [AppData, Auth],
  props     : {
    tooltip: {
      type   : String,
      default: ''
    },
    label: {
      type   : String,
      default: ''
    },
    hideLabel: {
      type   : Boolean,
      default: false
    },
    btnClass: {
      type   : String,
      default: 'white--text'
    },
    btnStyle: {
      type   : String,
      default: ''
    },
    color: {
      type   : String,
      default: 'orange'
    },
    icon: {
      type   : Boolean,
      default: false
    },
    iconName: {
      type   : String,
      default: 'mdi-receipt-text-plus-outline'
    },
    hideIcon: {
      type   : Boolean,
      default: false
    },
    small: {
      type   : Boolean,
      default: false
    },
    xSmall: {
      type   : Boolean,
      default: false
    },
    smallIcon: {
      type   : Boolean,
      default: false
    },
    smallSize: {
      type   : [Number, String],
      default: 32
    },
    large: {
      type   : Boolean,
      default: false
    },
    outline: {
      type   : Boolean,
      default: false
    },
    flat: {
      type   : Boolean,
      default: false
    },
    depressed: {
      type   : Boolean,
      default: false
    },
    absolute: {
      type   : Boolean,
      default: false
    },
    disabled: {
      type   : Boolean,
      default: false
    },
    block: {
      type   : Boolean,
      default: false
    },
    fixed: {
      type   : Boolean,
      default: false
    },
    fab: {
      type   : Boolean,
      default: false
    },
    dark: {
      type   : Boolean,
      default: false
    },
    tooltipPosition: {
      type   : String,
      default: 'top'
    },
    tooltipOpenDelay: {
      type   : Number,
      default: 150
    },
    tooltipCloseDelay: {
      type   : Number,
      default: 150
    },
    disableTooltip: {
      type   : Boolean,
      default: false
    },
    canIssueDocument: {
      type   : Boolean,
      default: false
    },
    canCancelFnb: {
      type   : Boolean,
      default: false
    },
    canCancelDocument: {
      type   : Boolean,
      default: false
    },
    canConvertReceipt: {
      type   : Boolean,
      default: false
    },
    documentUrl: {
      type   : String,
      default: ''
    },
    documentType: {
      type   : String,
      default: ''
    },
    documentId: {
      type   : [String, Number],
      default: undefined
    },
    order: {
      type   : Object,
      default: undefined
    },
    orderId: {
      type   : [String, Number],
      default: undefined
    },
    badgeOffsetX: {
      type   : [Number, String],
      default: undefined
    },
    badgeOffsetY: {
      type   : [Number, String],
      default: undefined
    },
    menuPosition: {
      type   : String,
      default: 'bottom'
    },
    menuOffsetY: {
      type   : Boolean,
      default: false
    },
    menuOffsetX: {
      type   : Boolean,
      default: false
    },
    openOnHover: {
      type   : Boolean,
      default: false
    },
    receipt: {
      type   : Boolean,
      default: false
    },
    invoice: {
      type   : Boolean,
      default: false
    },
    fnb: {
      type   : Boolean,
      default: false
    },
    from: {
      type   : String,
      default: ''
    }
  },
  dataStore: {},
  data() {
    return {
      menuActive: false
    }
  },
  computed: {
    NotifyTypeEnum() {
      return NotifyTypeEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },

    tooltipText() {
      if (this.tooltip) return this.tooltip

      return !this.canIssueDocument ? FiscalDocTypeEnum.isReceipt(this.documentType) ? this.$t('Common.Button.ManageReceipt') : this.$t('Common.Button.ManageInvoice') : this.$t('Common.Button.IssueDocument')
    },

    labelText() {
      if (this.label) return this.label.toLocaleUpperCase(this.$i18n.locale)

      return (!this.canIssueDocument ? FiscalDocTypeEnum.isReceipt(this.documentType) ? this.$t('Common.Button.ManageReceipt') : this.$t('Common.Button.ManageInvoice') : this.$t('Common.Button.IssueDocument')).toLocaleUpperCase(this.$i18n.locale)
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {
    onIssueDocumentClick(docType = FiscalDocTypeEnum.RECEIPT, print = null) {
      const canPrint = (print === false || print === true) ? print : FiscalDocTypeEnum.isReceipt(docType) ? this.ReceiptsPrintNotify : this.ReceiptsInvoicePrintNotify

      this.$emit('issue-document', {
        DocId         : null,
        DocType       : docType,
        Print         : canPrint,
        Email         : FiscalDocTypeEnum.isReceipt(docType) ? this.ReceiptsEmailNotify : this.ReceiptsInvoiceEmailNotify,
        EmailAddresses: [],
        Company       : null
      })
    },

    onPrintDocumentClick(docType = FiscalDocTypeEnum.RECEIPT) {
      this.$emit('print-document', {
        DocId         : this.documentId || null,
        DocType       : docType,
        Print         : true,
        Email         : false,
        EmailAddresses: [],
        Company       : null
      })
    },

    onEmailDocumentClick(docType = FiscalDocTypeEnum.RECEIPT) {
      this.$emit('email-document', {
        DocId         : this.documentId || null,
        DocType       : docType,
        Print         : false,
        Email         : true,
        From          : this.from || '',
        EmailAddresses: [],
        Company       : null
      })
    },

    onConvertReceiptToInvoiceClick(docId) {
      if (!docId) return

      this.$emit('convert-receipt', {DocId: docId})
    },

    hasValidDeliveryCardTransaction() {
      const orderTransactions = this.order?.PaymentTransactions || []
      return orderTransactions.some(trx => PaymentTypeEnum.isDeliveryCard(trx.PaymentType) && trx?.Paid === 'success' && trx?.Status === 'APPROVED')
    },

    onUpdateDocumentPaymentMethodsClick(orderId) {
      window.callAS(window.SocketCommand.Receipt.PaymentUpdate, {OrderId: orderId, Email: false, EmailAddresses: [], Manual: true})
    },

    onCancelDocumentClick(docId) {
      if (!docId) return

      this.executeWithPinPermissionManager(PinManagerEnum.DocumentCancel, (success) => {
        if (!success) return

        this.$emit('cancel-document', {DocId: docId})
      })
    },

    onViewDocumentClick(docUrl) {
      if (!docUrl) return

      this.$emit('view-document', {
        DocUrl: docUrl,
        DocId : this.documentId || null
      })
    }
  }
}
</script>

<style scoped>
.v-menu__content.menuable__content__active {
  max-height: 350px !important;
}
</style>
